import React, { useState, useRef } from 'react';
import { useQuery } from '@apollo/client';
import { CEREMONY_TEMPLATES_QUERY } from '../graphql/queries';
import {
  Box, Stack, Typography, TextField, Button, IconButton, Tooltip, Paper, Autocomplete, Chip,
  Accordion, AccordionSummary, AccordionDetails, Divider, Collapse, ToggleButton, ToggleButtonGroup, MenuItem,
  CircularProgress,
} from '@mui/material';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import CloseIcon from '@mui/icons-material/Close';
import { mediaUrl, uploadOfferingPhoto } from '../utils/media';
import PhotoStrip from './PhotoStrip';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';

const FALLBACK_LABELS = { package: 'Package', packages: 'Packages', item: 'Item', items: 'Items', unit: 'per event', sections: [] };

// Wording per service category, from the API (vendorOfferingFields._packageLabels)
export function packageLabelsFor(fieldDefs, category) {
  const all = fieldDefs?._packageLabels;
  return (all && (all[category] || all._default)) || FALLBACK_LABELS;
}

const inr = (n) => `₹${Number(n).toLocaleString('en-IN')}`;
let tempId = 0;
const newKey = () => `new-${Date.now()}-${tempId++}`;
const blank = (v) => v === '' || v === null || v === undefined;

// 'items': the package price is the sum of its items' costs; 'fixed': the vendor enters it
const isRollup = (p) => p.pricing === 'items';
export const itemsTotal = (items) => Math.round((items || []).reduce((s, it) => s + (Number(it.price) || 0), 0) * 100) / 100;
// Price to show for a package, including one being edited
const packagePrice = (p) => (isRollup(p) ? itemsTotal(p.items) : p.price);

// Strip GraphQL __typename and client-only keys before sending packages back to the API
export function packagesToInput(packages) {
  return (packages || []).map(p => ({
    id: p.id && !String(p.id).startsWith('new-') ? p.id : null,
    name: p.name,
    description: p.description || null,
    pricing: isRollup(p) ? 'items' : 'fixed',
    ceremonyType: p.ceremonyType || null,
    // The API works out the total for 'items' packages
    price: isRollup(p) || blank(p.price) ? null : Number(p.price),
    priceUnit: p.priceUnit || null,
    items: (p.items || []).map(i => ({
      id: i.id && !String(i.id).startsWith('new-') ? i.id : null,
      section: i.section || null,
      name: i.name,
      description: i.description || null,
      price: i.price === '' || i.price === null || i.price === undefined ? null : Number(i.price),
      photos: i.photos || [],
    })),
  }));
}

// Problems to fix before saving (the API checks the same rules)
export function packageProblems(packages, labels) {
  const problems = [];
  (packages || []).forEach((p, i) => {
    const name = p.name?.trim() || `${labels.package} ${i + 1}`;
    if (!p.name?.trim()) problems.push(`${labels.package} ${i + 1} needs a name`);
    if (isRollup(p)) {
      if (!(p.items || []).length) problems.push(`Add at least one ${labels.item.toLowerCase()} with a cost to "${name}"`);
    } else if (blank(p.price) || Number(p.price) < 0) {
      problems.push(`Enter a price for "${name}"`);
    }
    (p.items || []).forEach((it, j) => {
      const itemName = it.name?.trim() || `${labels.item} ${j + 1}`;
      if (!it.name?.trim()) problems.push(`${labels.item} ${j + 1} in "${name}" needs a name`);
      if (isRollup(p) && (blank(it.price) || Number(it.price) < 0)) problems.push(`Enter a cost for "${itemName}" in "${name}" (0 if included)`);
    });
  });
  return problems;
}

const move = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [x] = next.splice(from, 1);
  next.splice(to, 0, x);
  return next;
};

// Standard-ceremony checklist categories -> ritual item sections
const MATERIAL_SECTION = { material: 'Pooja materials', decoration: 'Decoration', personnel: 'Priests & helpers', equipment: 'Other' };
const titleCase = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

// A ceremony package started from a standard ceremony: its materials and priests/helpers as
// items for the vendor to price
function ceremonyFromTemplate(t, unit) {
  const materials = (t.defaultMaterials || []).map(m => ({
    id: newKey(), section: MATERIAL_SECTION[m.category] || 'Pooja materials', name: m.item_name, description: m.quantity || '', price: '',
  }));
  const people = (t.defaultPersonnel || []).map(p => ({
    id: newKey(), section: 'Priests & helpers', name: titleCase(p.role), description: p.count ? `${p.count} needed` : '', price: '',
  }));
  return { id: newKey(), name: t.displayName, description: t.description || '', pricing: 'items', price: '', priceUnit: unit, ceremonyType: t.ceremonyType, items: [...people, ...materials] };
}

// ── Item photos (vendor) ─────────────────────────────────────────────────────
// Photos of one item (a dish, the mandap...), uploaded straight away; saved with the offering
const MAX_ITEM_PHOTOS = 4;

function ItemPhotosEditor({ itemName, photos, onChange }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const fileInput = useRef(null);

  const addFiles = async (files) => {
    const room = MAX_ITEM_PHOTOS - photos.length;
    const chosen = [...files].slice(0, room);
    if (!chosen.length) return;
    setBusy(true);
    setError(files.length > room ? `Only ${MAX_ITEM_PHOTOS} photos per item — the first ${room} were added.` : '');
    const added = [];
    for (const file of chosen) {
      try {
        added.push(await uploadOfferingPhoto(file));
      } catch (e) {
        setError(e.message);
      }
    }
    if (added.length) onChange((current) => [...current, ...added].slice(0, MAX_ITEM_PHOTOS));
    setBusy(false);
  };

  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 0.75, ml: { md: 1 } }}>
      {photos.map((url, k) => (
        <Box key={url} sx={{ position: 'relative' }}>
          <Box
            role="img"
            aria-label={`Photo ${k + 1} of ${itemName}`}
            sx={{ width: 52, height: 52, borderRadius: 1.5, border: '1px solid #e0e0e0', background: `center / cover no-repeat url("${mediaUrl(url)}")` }}
          />
          <IconButton
            size="small"
            aria-label={`Remove photo ${k + 1} of ${itemName}`}
            onClick={() => onChange(photos.filter(u => u !== url))}
            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: '#fff', boxShadow: 1, p: 0.25, '&:hover': { bgcolor: '#fff' } }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ))}
      {photos.length < MAX_ITEM_PHOTOS && (
        <>
          <input
            ref={fileInput}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
          />
          <Button size="small" variant="text" disabled={busy} onClick={() => fileInput.current?.click()}
            startIcon={busy ? <CircularProgress size={14} /> : <AddAPhotoIcon fontSize="small" />}>
            {busy ? 'Uploading…' : photos.length ? 'Add photo' : `Add photos of ${itemName.toLowerCase()}`}
          </Button>
        </>
      )}
      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Stack>
  );
}

// ── Editor (vendor) ──────────────────────────────────────────────────────────
export function OfferingPackagesEditor({ labels, packages, onChange, defaultUnit, category }) {
  const [quickAdd, setQuickAdd] = useState({}); // package index -> { open, section, text }
  // Rituals: packages are ceremonies, started from / tagged with the standard ceremonies
  const isRituals = category === 'rituals';
  const { data: tmplData } = useQuery(CEREMONY_TEMPLATES_QUERY, { skip: !isRituals });
  const templates = tmplData?.ceremonyTemplates || [];
  const [startFrom, setStartFrom] = useState('');

  // The latest packages: photo uploads finish after other edits, and must not undo them
  const latest = useRef(packages);
  latest.current = packages;
  const setPkg = (i, patch) => onChange(latest.current.map((p, k) => (k === i ? { ...p, ...patch } : p)));
  const setItem = (i, j, patch) => setPkg(i, { items: latest.current[i].items.map((it, k) => (k === j ? { ...it, ...patch } : it)) });
  // patch may be a function of the item (e.g. adding photos to those it has now)
  const updateItem = (i, j, fn) => {
    const next = latest.current.map((p, k) => (k === i ? { ...p, items: p.items.map((it, m) => (m === j ? fn(it) : it)) } : p));
    latest.current = next;
    onChange(next);
  };
  const addPkg = () => onChange([...packages, { id: newKey(), name: '', description: '', pricing: 'items', price: '', priceUnit: defaultUnit || labels.unit, items: [] }]);
  // Switching to a fixed price starts from the current item total, so nothing jumps
  const setPricing = (i, pricing) => {
    if (!pricing) return;
    setPkg(i, pricing === 'fixed' && blank(packages[i].price) ? { pricing, price: itemsTotal(packages[i].items) } : { pricing });
  };
  const addItem = (i, section = '') => setPkg(i, { items: [...packages[i].items, { id: newKey(), section, name: '', description: '', price: '' }] });
  const duplicate = (i) => {
    const p = packages[i];
    const copy = { ...p, id: newKey(), name: `${p.name} (copy)`, items: p.items.map(it => ({ ...it, id: newKey() })) };
    onChange([...packages.slice(0, i + 1), copy, ...packages.slice(i + 1)]);
  };
  const applyQuickAdd = (i) => {
    const q = quickAdd[i] || {};
    const lines = (q.text || '').split('\n').map(l => l.trim()).filter(Boolean);
    if (!lines.length) return;
    // "Name - 40" or "Name | 40" adds an optional item price
    const items = lines.map(line => {
      const m = line.match(/^(.*?)\s*[-|–]\s*₹?\s*(\d+(?:\.\d+)?)\s*$/);
      return { id: newKey(), section: q.section || '', name: (m ? m[1] : line).trim(), description: '', price: m ? Number(m[2]) : '' };
    });
    setPkg(i, { items: [...packages[i].items, ...items] });
    setQuickAdd(s => ({ ...s, [i]: { open: false, section: q.section, text: '' } }));
  };

  const addFromTemplate = () => {
    const t = templates.find(x => x.ceremonyType === startFrom);
    if (!t) return;
    onChange([...packages, ceremonyFromTemplate(t, defaultUnit || labels.unit)]);
    setStartFrom('');
  };

  return (
    <Stack spacing={2}>
      {isRituals && (
        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover' }}>
          <Typography variant="body2" mb={1}>
            Each ceremony you offer is listed here with its ritual items and their cost. Start from a standard ceremony
            to get its usual items, then set your prices; the ceremony price is their total.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <TextField select size="small" label="Start from a standard ceremony" value={startFrom} sx={{ minWidth: 280 }}
              onChange={e => setStartFrom(e.target.value)}>
              {templates.map(t => (
                <MenuItem key={t.ceremonyType} value={t.ceremonyType} disabled={packages.some(p => p.ceremonyType === t.ceremonyType)}>
                  {t.displayName}{packages.some(p => p.ceremonyType === t.ceremonyType) ? ' (added)' : ''}
                </MenuItem>
              ))}
            </TextField>
            <Button variant="contained" size="small" startIcon={<AddIcon />} disabled={!startFrom} onClick={addFromTemplate}>Add ceremony</Button>
          </Stack>
        </Paper>
      )}
      {packages.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          No {labels.packages.toLowerCase()} yet. Add one to list its {labels.items.toLowerCase()} and price.
        </Typography>
      )}
      {packages.map((p, i) => {
        const q = quickAdd[i] || {};
        const rollup = isRollup(p);
        return (
          <Paper key={p.id} variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <TextField size="small" label={`${labels.package} name *`} value={p.name} sx={{ flex: 2 }}
                onChange={e => setPkg(i, { name: e.target.value })} placeholder={labels.package === 'Menu' ? 'e.g. Muhurtham lunch menu' : ''} />
              {rollup ? (
                <TextField size="small" label="Total (₹)" value={itemsTotal(p.items)} sx={{ flex: 1 }}
                  InputProps={{ readOnly: true }} helperText={`Sum of ${p.items.length} ${(p.items.length === 1 ? labels.item : labels.items).toLowerCase()}`} />
              ) : (
                <TextField size="small" type="number" label="Price (₹) *" value={p.price} sx={{ flex: 1 }} inputProps={{ min: 0 }}
                  onChange={e => setPkg(i, { price: e.target.value })} />
              )}
              <TextField size="small" label="Unit" value={p.priceUnit} sx={{ flex: 1 }}
                onChange={e => setPkg(i, { priceUnit: e.target.value })} />
              <Stack direction="row">
                <Tooltip title="Move up"><span><IconButton size="small" disabled={i === 0} onClick={() => onChange(move(packages, i, i - 1))}><ArrowUpwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title="Move down"><span><IconButton size="small" disabled={i === packages.length - 1} onClick={() => onChange(move(packages, i, i + 1))}><ArrowDownwardIcon fontSize="small" /></IconButton></span></Tooltip>
                <Tooltip title={`Duplicate ${labels.package.toLowerCase()}`}><IconButton size="small" onClick={() => duplicate(i)}><ContentCopyIcon fontSize="small" /></IconButton></Tooltip>
                <Tooltip title={`Remove ${labels.package.toLowerCase()}`}><IconButton size="small" color="error" onClick={() => onChange(packages.filter((_, k) => k !== i))}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
              </Stack>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mt={1.5}>
              <TextField size="small" label="Description" value={p.description} fullWidth
                onChange={e => setPkg(i, { description: e.target.value })} />
              {isRituals && (
                <TextField select size="small" label="Standard ceremony" value={p.ceremonyType || ''} sx={{ minWidth: 240 }}
                  helperText="Links it to the ceremony families plan"
                  onChange={e => setPkg(i, { ceremonyType: e.target.value || null })}>
                  <MenuItem value="">Other / custom</MenuItem>
                  {templates.map(t => <MenuItem key={t.ceremonyType} value={t.ceremonyType}>{t.displayName}</MenuItem>)}
                </TextField>
              )}
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} mt={1.5}>
              <ToggleButtonGroup size="small" exclusive color="primary" value={rollup ? 'items' : 'fixed'} onChange={(_, v) => setPricing(i, v)}>
                <ToggleButton value="items">Sum of {labels.items.toLowerCase()}</ToggleButton>
                <ToggleButton value="fixed">Fixed price</ToggleButton>
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary">
                {rollup
                  ? `Each ${labels.item.toLowerCase()} has a cost (0 if included); the ${labels.package.toLowerCase()} price is their total.`
                  : `You set the ${labels.package.toLowerCase()} price; ${labels.item.toLowerCase()} prices are optional add-ons.`}
              </Typography>
            </Stack>

            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mt={2} mb={1}>
              {labels.items.toUpperCase()} ({p.items.length})
            </Typography>
            <Stack spacing={1}>
              {p.items.map((it, j) => (
                <Box key={it.id}>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }}>
                  <Autocomplete
                    freeSolo size="small" sx={{ width: { md: 180 } }}
                    options={labels.sections || []}
                    inputValue={it.section || ''}
                    onInputChange={(_, v) => setItem(i, j, { section: v })}
                    renderInput={params => <TextField {...params} label="Section" />}
                  />
                  <TextField size="small" label={`${labels.item} *`} value={it.name} sx={{ flex: 2 }}
                    onChange={e => setItem(i, j, { name: e.target.value })} />
                  <TextField size="small" label="Details" value={it.description} sx={{ flex: 2 }}
                    onChange={e => setItem(i, j, { description: e.target.value })} />
                  <TextField size="small" type="number" label={rollup ? 'Cost (₹) *' : 'Add-on price'} value={it.price ?? ''} sx={{ width: { md: 140 } }}
                    inputProps={{ min: 0 }} onChange={e => setItem(i, j, { price: e.target.value })} />
                  <Stack direction="row">
                    <IconButton size="small" disabled={j === 0} onClick={() => setPkg(i, { items: move(p.items, j, j - 1) })}><ArrowUpwardIcon fontSize="small" /></IconButton>
                    <IconButton size="small" color="error" onClick={() => setPkg(i, { items: p.items.filter((_, k) => k !== j) })}><DeleteIcon fontSize="small" /></IconButton>
                  </Stack>
                </Stack>
                <ItemPhotosEditor
                  itemName={it.name || `${labels.item} ${j + 1}`}
                  photos={it.photos || []}
                  onChange={(photos) => updateItem(i, j, (it) => ({ ...it, photos: typeof photos === 'function' ? photos(it.photos || []) : photos }))}
                />
                </Box>
              ))}
            </Stack>
            <Stack direction="row" spacing={1} mt={1}>
              <Button size="small" startIcon={<AddIcon />} onClick={() => addItem(i, p.items[p.items.length - 1]?.section || '')}>
                Add {labels.item.toLowerCase()}
              </Button>
              <Button size="small" startIcon={<PlaylistAddIcon />} onClick={() => setQuickAdd(s => ({ ...s, [i]: { ...q, open: !q.open } }))}>
                Add several
              </Button>
            </Stack>
            <Collapse in={Boolean(q.open)}>
              <Stack spacing={1} mt={1} p={1.5} sx={{ bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  One {labels.item.toLowerCase()} per line. Add {rollup ? 'its cost' : 'a price'} with a dash, e.g. "Gulab jamun - 40".
                  {rollup && ' Lines without a cost are added blank for you to fill in.'}
                </Typography>
                <Autocomplete
                  freeSolo size="small" options={labels.sections || []} inputValue={q.section || ''}
                  onInputChange={(_, v) => setQuickAdd(s => ({ ...s, [i]: { ...s[i], section: v } }))}
                  renderInput={params => <TextField {...params} label="Section for these (optional)" />}
                />
                <TextField size="small" multiline minRows={3} value={q.text || ''} placeholder={labels.item === 'Dish' ? 'Sambar\nRasam\nAvial' : ''}
                  onChange={e => setQuickAdd(s => ({ ...s, [i]: { ...s[i], text: e.target.value } }))} />
                <Box><Button size="small" variant="contained" onClick={() => applyQuickAdd(i)}>Add to {p.name || labels.package.toLowerCase()}</Button></Box>
              </Stack>
            </Collapse>
          </Paper>
        );
      })}
      <Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={addPkg}>Add {labels.package.toLowerCase()}</Button>
      </Box>
    </Stack>
  );
}

// ── Read-only view (vendor list, families, matchmakers, admin) ───────────────
export function OfferingPackagesView({ labels, packages, dense = false, onBook }) {
  if (!packages?.length) return null;
  return (
    <Box mt={1.5}>
      <Typography variant="caption" fontWeight={700} color="text.secondary">
        {labels.packages.toUpperCase()} ({packages.length})
      </Typography>
      {packages.map(p => {
        // Group items by section, keeping the vendor's order
        const groups = [];
        p.items.forEach(it => {
          const key = it.section || '';
          let g = groups.find(x => x.section === key);
          if (!g) { g = { section: key, items: [] }; groups.push(g); }
          g.items.push(it);
        });
        // The vendor's photos of the items, captioned with the item
        const photos = p.items.flatMap(it => (it.photos || []).map(url => ({ url, caption: it.name })));
        return (
          <Accordion key={p.id} disableGutters variant="outlined" sx={{ mt: 1, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%', pr: 1 }} flexWrap="wrap" useFlexGap>
                {photos[0] && (
                  <Box aria-hidden sx={{ width: 40, height: 40, borderRadius: 1, flexShrink: 0, background: `center / cover no-repeat url("${mediaUrl(photos[0].url)}")` }} />
                )}
                <Typography fontWeight={600} sx={{ flex: 1, minWidth: 140 }}>{p.name}</Typography>
                {photos.length > 0 && <Typography variant="caption" color="text.secondary">{photos.length} photo{photos.length === 1 ? '' : 's'}</Typography>}
                <Chip size="small" color="primary" variant="outlined" label={`${inr(packagePrice(p))} ${p.priceUnit}`} />
                {isRollup(p) && <Typography variant="caption" color="text.secondary">sum of {labels.items.toLowerCase()}</Typography>}
                <Typography variant="caption" color="text.secondary">{p.items.length} {(p.items.length === 1 ? labels.item : labels.items).toLowerCase()}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              {p.description && <Typography variant="body2" color="text.secondary" mb={1}>{p.description}</Typography>}
              {photos.length > 0 && <Box mb={1.5}><PhotoStrip photos={photos} size={72} max={8} /></Box>}
              {onBook && (
                <Button size="small" variant="outlined" sx={{ mb: 1 }} onClick={() => onBook(p)}>
                  Book this {labels.package.toLowerCase()}
                </Button>
              )}
              {groups.map((g, gi) => (
                <Box key={g.section || `g${gi}`} mb={1}>
                  {g.section && (
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="caption" fontWeight={700} color="primary.main">{g.section}</Typography>
                      {isRollup(p) && groups.length > 1 && (
                        <Typography variant="caption" fontWeight={700} color="primary.main">{inr(itemsTotal(g.items))}</Typography>
                      )}
                    </Stack>
                  )}
                  {g.items.map(it => (
                    <Stack key={it.id} direction="row" spacing={1} justifyContent="space-between" sx={{ py: dense ? 0 : 0.25 }}>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">
                          • {it.name}
                          {it.description && <Typography component="span" variant="caption" color="text.secondary"> — {it.description}</Typography>}
                        </Typography>
                        <PhotoStrip photos={it.photos} caption={it.name} size={28} max={3} />
                      </Stack>
                      {it.price !== null && it.price !== undefined && (
                        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>{inr(it.price)}</Typography>
                      )}
                    </Stack>
                  ))}
                  {gi < groups.length - 1 && <Divider sx={{ mt: 1 }} />}
                </Box>
              ))}
              {isRollup(p) && (
                <>
                  <Divider />
                  <Stack direction="row" justifyContent="space-between" mt={1}>
                    <Typography variant="body2" fontWeight={700}>{labels.package} total</Typography>
                    <Typography variant="body2" fontWeight={700}>{inr(packagePrice(p))} {p.priceUnit}</Typography>
                  </Stack>
                </>
              )}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Box>
  );
}
